import React, { useReducer, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, CircularProgress, MenuItem, Select } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';

import LeadersPlayerStatColumn from './playerStatColumn';
import { MenuProps } from '../components/common';
import { enrichPlayerData } from '../components/utilities';
import PlayerStatDialog from './status';

import SeasonContext from '../../../common/Context';
import { getFilteredArray } from '../../../helpers/coach';
import { setAllPlayersStatsUnified } from '../../../helpers/coachSlice';

//Missing more categories
const statCategory = [
    { id: 'player_games', title: 'Games Played' },
    { id: 'games_starting', title: 'Starting 11' },
    { id: 'games_minutes', title: 'Minutes Played' },
    { id: 'goal', title: 'Goals' },

    { id: 'passes', title: 'Passes' },
    { id: 'successful_passes', title: 'Successful Passes' },
    { id: 'passes_for_goals', title: 'Assists' },
    { id: 'key_passes', title: 'Key Passes' },

    { id: 'through_passes', title: 'Through Passes' },
    { id: 'through_passes_successful', title: 'Through Passes Successful' },
    { id: 'passes_for_shots', title: 'Passes For Shots' },
    { id: 'shot', title: 'Shots' },

    { id: 'shot_on_target', title: 'Shots On Target' },
    { id: 'shot_off_target', title: 'Shots Off Target' },
    { id: 'shot_on_box', title: 'Shots On Box' },
    { id: 'shot_out_of_box', title: 'Long Shots' },

    { id: 'free_kick', title: 'Free Kicks' },
    { id: 'crosses', title: 'Crosses' },
    { id: 'penalties', title: 'Penalties' },
    { id: 'penalties_missed', title: 'Missed Penalties' },

    { id: 'dribble', title: 'Dribbles' },
    { id: 'dribble_successful', title: 'Successful 1v1 Attacking' },
    { id: 'dribble_unsuccessful', title: 'Unsuccessful 1v1 Attacking' },
    { id: 'turnover', title: 'Turnovers' },

    { id: 'turnover_on_defensive_court', title: 'Turnovers on Defensive Court' },
    { id: 'turnover_on_offensive_court', title: 'Turnovers on Offensive Court' },
    { id: 'offside', title: 'Offside' },

    { id: 'tackle', title: 'Tackles' },
    { id: 'tackle_on_offensive_court', title: 'Tackles on Offensive Court' },
    { id: 'tackle_on_defensive_court', title: 'Tackles on Defensive Court' },
    { id: 'saved', title: 'Saved & Super Saved' },

    { id: 'clearance', title: 'Clearance' },
    { id: 'interception', title: 'Interceptions' },
    { id: 'interception_on_defensive_court', title: 'Interceptions on Defensive Court' },
    { id: 'interception_on_offensive_court', title: 'Interceptions on Offensive Court' },

    { id: 'fouls', title: 'Fouls' },
    { id: 'draw_fouls', title: 'Draw Fouls' },
    { id: 'red_cards', title: 'Red Cards' },
    { id: 'yellow_cards', title: 'Yellow Cards' }
];

const Leaders = ({ t }) => {
    const { seasonFilter } = React.useContext(SeasonContext);

    const [state, setState] = useReducer((old, action) => ({ ...old, ...action }), {
        hoverIndex: -1,
        leagueFilter: 'none',
        loading: false,
        teamFilter: 'none',
        playerFilter: 'none'
    });

    const [selectedPlayers, setSelectedPlayers] = useState([]);
    const [filteredPlayers, setFilteredPlayers] = useState([]);

    const teamsList = useSelector((state) => state.coach.teamsList);
    const leagueList = useSelector((state) => state.coach.leagueList);
    const gamesList = useSelector((state) => state.coach.gamesList);
    const allPlayersStatsUnified = useSelector((state) => state.coach.allPlayersStatsUnified);

    const { leagueFilter, teamFilter, playerFilter } = state;

    const [loading, setLoading] = useState(true);
    const [displayOption, setDisplayOption] = useState('total');
    const [currentPlayer, setCurrentPlayer] = useState(null);
    const [statOpen, setStatOpen] = useState(false);
    const [gameList, setGameList] = useState([]);
    const [seasonTeamsList, setSeasonTeamsList] = useState([]);
    const [seasonLeaguesList, setSeasonLeaguesList] = useState([]);

    const dispatch = useDispatch();

    const handleChange = (prop) => (e) => {
        setState({ ...state, [prop]: e.target.value });
    };

    const handleDisplayStatDialog = (player) => {
        // Find team from teamsList for fallback data
        const playerTeam = teamsList.find((team) => team.team_name === player.team_name);

        // Filter games to only include games where this player's team participated
        // This ensures we only pass relevant games to the modal, not all games in the season
        const playerTeamId = playerTeam?.team_id || player.team_id;
        const playerGames = gameList.filter(
            (game) => game.home_team_id === playerTeamId || game.away_team_id === playerTeamId
        );


        // Use shared utility for consistent player data enrichment
        const enrichedPlayer = enrichPlayerData(player, playerTeam);
        enrichedPlayer.filteredGames = playerGames; // Pass filtered games with player data

        setCurrentPlayer(enrichedPlayer);
        setStatOpen(true);
    };

    const updatePlayer = (newPlayer) => {
        if (!seasonFilter) return;
        if (allPlayersStatsUnified && allPlayersStatsUnified.hasOwnProperty(seasonFilter === 'none' ? 'all' : seasonFilter.name)) {
            dispatch(
                setAllPlayersStatsUnified({
                    season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name,
                    data: allPlayersStatsUnified[seasonFilter === 'none' ? 'all' : seasonFilter.name].map((player) => {
                        if (player.player_id === newPlayer.player_id) {
                            return { ...player, ...newPlayer };
                        }
                        return player;
                    })
                })
            );
        }
    };

    useEffect(() => {
        if (!seasonFilter) return;
        const seasonKey = seasonFilter === 'none' ? 'all' : seasonFilter.name;
        const hasSeasonData = allPlayersStatsUnified && allPlayersStatsUnified.hasOwnProperty(seasonKey);

        if (!hasSeasonData) {
            setLoading(true);
            return;
        }

        const seasonData = allPlayersStatsUnified[seasonKey];
        if (seasonData && seasonData.length > 0) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [allPlayersStatsUnified, seasonFilter]);

    useEffect(() => {
        (async () => {
            if (!seasonFilter) return;
            if (allPlayersStatsUnified && allPlayersStatsUnified.hasOwnProperty(seasonFilter === 'none' ? 'all' : seasonFilter.name)) {
                const filtered_arr = getFilteredArray(allPlayersStatsUnified[seasonFilter === 'none' ? 'all' : seasonFilter.name], seasonFilter, null, null, teamFilter, playerFilter);
                setSelectedPlayers(filtered_arr);
            }
        })();
    }, [allPlayersStatsUnified, leagueFilter, teamFilter, playerFilter, seasonFilter]);

    useEffect(() => {
        (async () => {
            if (!seasonFilter) return;
            setState({ playerFilter: 'none' });
            if (allPlayersStatsUnified && allPlayersStatsUnified.hasOwnProperty(seasonFilter === 'none' ? 'all' : seasonFilter.name)) {
                const filtered_arr = getFilteredArray(allPlayersStatsUnified[seasonFilter === 'none' ? 'all' : seasonFilter.name], seasonFilter, null, null, teamFilter, null);
                setFilteredPlayers(filtered_arr);
            }
        })();
    }, [allPlayersStatsUnified, seasonFilter, leagueFilter, teamFilter]);

    useEffect(() => {
        (async () => {
            if (teamsList.length) {
                const filtered_arr = getFilteredArray(teamsList, seasonFilter, leagueFilter, null, null);
                setSeasonTeamsList(filtered_arr);
                setState({ teamFilter: 'none' });
                setState({ playerFilter: 'none' });
            }
        })();
    }, [leagueFilter, teamsList]);

    useEffect(() => {
        (async () => {
            if (!seasonFilter) return;
            if (leagueList.length) {
                const filteredLeagues = leagueList.filter((league) => league.season_id === seasonFilter.id);
                setSeasonLeaguesList(filteredLeagues);
                setState({ leagueFilter: 'none' });
            }
            if (teamsList.length) {
                const filtered_arr = getFilteredArray(teamsList, seasonFilter, leagueFilter, null, null);
                setSeasonTeamsList(filtered_arr);
                setState({ teamFilter: 'none' });
            }
            if (gamesList.length) {
                const filtered_games = gamesList.filter((game) => game.season_id === seasonFilter.id);
                setGameList(filtered_games);
            }
            setState({ playerFilter: 'none' });
        })();
    }, [seasonFilter, leagueList, gamesList]);

    // Guard: Return loading state if seasonFilter is not available
    if (!seasonFilter) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '98%', margin: '0 auto', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Box sx={{ padding: '24px 24px 24px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <p className="page-title">{t('Rankings')}</p>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', paddingLeft: '30px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <p className="select-narrator">{t('Teams')}</p>
                            <Select
                                value={teamFilter}
                                onChange={handleChange('teamFilter')}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{ outline: 'none', height: '36px', width: '220px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                            >
                                <MenuItem key="0" value="none">
                                    {t('All')}
                                </MenuItem>
                                {seasonTeamsList.map((team, index) => (
                                    <MenuItem key={index + 1} value={team}>
                                        {team.team_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <p className="select-narrator">{t('Display')}</p>
                            <Select
                                value={displayOption}
                                onChange={(e) => setDisplayOption(e.target.value)}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{ outline: 'none', height: '36px', width: '110px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                            >
                                <MenuItem key="0" value="total">
                                    {t('Total')}
                                </MenuItem>
                                <MenuItem key="1" value="average">
                                    {t('Average')}
                                </MenuItem>
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <p className="select-narrator">{t('Player')}</p>
                            <Select
                                value={playerFilter}
                                onChange={handleChange('playerFilter')}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{ outline: 'none', height: '36px', width: '110px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                            >
                                <MenuItem key="0" value="none">
                                    {t('All')}
                                </MenuItem>
                                {filteredPlayers.map((player, index) => (
                                    <MenuItem key={index + 1} value={player}>
                                        {player.player_full_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Box>
                </Box>
            </Box>
            {loading && (
                <div style={{ width: '100%', flex: 1, zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && selectedPlayers.length > 0 && (
                <Box
                    sx={{
                        width: '100%',
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        display: 'flex',
                        padding: '0 24px 20px 24px'
                    }}
                >
                    <Box sx={{ fontSize: '0.8rem', display: 'grid', gridTemplateColumns: 'auto auto auto auto', gap: '4px', width: '100%' }}>
                        {statCategory.map((item) => (
                            <LeadersPlayerStatColumn
                                key={item.id}
                                list={selectedPlayers}
                                isTotal={item.id === 'player_games' ? true : displayOption === 'total'}
                                option={item.id}
                                title={item.title}
                                onClick={handleDisplayStatDialog}
                                t={t}
                            />
                        ))}
                    </Box>
                </Box>
            )}
            <PlayerStatDialog
                open={statOpen}
                t={t}
                onClose={() => {
                    setStatOpen(false);
                }}
                player={currentPlayer}
                games={currentPlayer?.filteredGames || []}
                gameIds={currentPlayer?.filteredGames?.map((item) => item.id) || []}
                updatePlayer={updatePlayer}
                page="yes"
                statsData={allPlayersStatsUnified[seasonFilter === 'none' ? 'all' : seasonFilter.name] || []}
                initialState={currentPlayer}
            />
        </Box>
    );
};

export default Leaders;
